import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Printer, FileText, Tag, Hash } from 'lucide-react';
import Button from './ui/Button';

interface PrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (options: PrintOptions) => void;
  note: {
    title: string;
    content: string;
    tags?: string[];
  };
}

export interface PrintOptions {
  includeTitle: boolean;
  includeTags: boolean;
  includeContent: boolean;
}

const PrintModal: React.FC<PrintModalProps> = ({ isOpen, onClose, onPrint, note }) => {
  const [options, setOptions] = useState<PrintOptions>({
    includeTitle: true,
    includeTags: false, // Disabled for now
    includeContent: true,
  });

  const handleOptionChange = (key: keyof PrintOptions, value: boolean) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const handlePrint = () => {
    // Check if at least one option is selected
    if (!options.includeTitle && !options.includeTags && !options.includeContent) {
      return; // Don't proceed if nothing is selected
    }
    onPrint(options);
    onClose();
  };

  const isAtLeastOneSelected = options.includeTitle || options.includeTags || options.includeContent;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-background border rounded-lg shadow-lg max-w-md w-full p-6"
        >
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Print Options</h2>
              <p className="text-sm text-muted-foreground">Choose what to include in the print</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">Title</div>
                  <div className="text-sm text-muted-foreground truncate max-w-48">
                    {note.title}
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={options.includeTitle}
                onChange={(e) => handleOptionChange('includeTitle', e.target.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 opacity-50">
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">Tags</div>
                  <div className="text-sm text-muted-foreground">
                    Coming soon
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={options.includeTags}
                disabled
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 cursor-not-allowed"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <Hash className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">Content</div>
                  <div className="text-sm text-muted-foreground">
                    Note content and code blocks
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={options.includeContent}
                onChange={(e) => handleOptionChange('includeContent', e.target.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
              />
            </div>
          </div>

          {!isAtLeastOneSelected && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive">
                Please select at least one item to include in the print.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handlePrint}
              disabled={!isAtLeastOneSelected}
              className="bg-primary hover:bg-primary/90"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrintModal;
